import React, { useState, useEffect, useMemo } from "react";
import { Form, Select } from "antd";
import styled from "styled-components";
import { useToast } from "../../store/ToastProvider";
import { useReportConfig } from "../../hooks/useBuildReportConfig";
import { usePost } from "../../hooks/usePost";
import { usePoll } from "../../hooks/usePoll";
import { CompassParagraph } from "../../components/common/styledComponents";
import { Filter as Option } from "../../types/PageFilterTypes";
import { ReportCategory } from "../../enums/Reports";
import { ModalState, HeroKind } from "../../enums/Modal";
import { ReportItem } from "./reportsConfig";
import { defaultReportItem } from "./reportsConfig";

import {
  ReportJobStartedConfig,
  ReportJobStatus,
  ReportStatuses,
  CreateReportParams,
} from "../../models/reportModels";

import { greyText, darkGreen } from "../../styles";
import { MESSAGES } from "../../messages";
import calculatingAnimation from "../../assets/lottie/generating_report.json";
import RunJobModalImage from "../../assets/svg/report_creation_placeholder.svg";
import { Status } from "../../enums/Status";
import reportIcon from "../../assets/svg/reports_list.svg";
import {
  CompassModal,
  ModalContent,
  ModalFooter,
  PreserveSpace,
  useCompassModal,
  defineModalConfig,
} from "../../components/common/Modal";

// =============================================================================
// STYLES
// =============================================================================

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StyledFormWrapper = styled.div`
  width: 100%;
  max-width: 600px;
`;

// =============================================================================
// MODAL CONFIG
// =============================================================================

type ReportContext = { reportType: ReportCategory; dotCounter: number };

const CREATE_REPORT_MODAL_CONFIG = defineModalConfig<ModalState, ReportContext>(
  {
    title: "Generate Report",
    titleIcon: reportIcon,
    modalSize: "md",
    size: "lg",
    states: {
      [ModalState.IDLE]: {
        hero: {
          kind: HeroKind.IMAGE,
          source: RunJobModalImage,
          size: 230,
          padding: 100,
          offsetY: -20,
          alt: "Generate Report",
          title: MESSAGES.GENERATE_REPORT_TITLE["NOT_GENERATING"],
          description: (ctx) =>
            MESSAGES.GENERATE_REPORT_DESCRIPTION?.[
              ctx.reportType as keyof typeof MESSAGES.GENERATE_REPORT_DESCRIPTION
            ]?.["NOT_GENERATING"] || "",
        },
      },
      [ModalState.GENERATING]: {
        isProcessing: true,
        hero: {
          size: 230,
          kind: HeroKind.LOTTIE,
          animation: calculatingAnimation,
          title: MESSAGES.GENERATE_REPORT_TITLE["GENERATING"],
          description: (ctx) =>
            MESSAGES.GENERATE_REPORT_DESCRIPTION?.[
              ctx.reportType as keyof typeof MESSAGES.GENERATE_REPORT_DESCRIPTION
            ]?.["GENERATING"] || "",
          suffix: (ctx) =>
            [1, 2, 3].map((index) => (
              <CompassParagraph
                key={index}
                color={ctx.dotCounter >= index ? darkGreen : "transparent"}
                mr={-2}
                fontSize={20}
                bold={true}
              >
                .
              </CompassParagraph>
            )),
        },
      },
    },
  }
);

// =============================================================================
// COMPONENT
// =============================================================================

export const CreateReportModal: React.FC<ModalComponentProps> = ({
  show,
  handleClose,
  reportType = ReportCategory.TCFD,
}) => {
  const [form] = Form.useForm();
  const { addToast } = useToast();
  const {
    reportItem: reportParams,
    fetchReportParameters,
    isLoading,
    error,
  } = useReportConfig(reportType);
  const {
    data: triggerReportCreationResponse,
    error: createReportError,
    sendData: triggerReportCreation,
  } = usePost<ReportJobStartedConfig, CreateReportParams>();
  const {
    data: reportJobStatus,
    error: reportJobStatusError,
    startPolling,
  } = usePoll<ReportJobStatus, ReportJobStartedConfig>(
    (response) => response?.Status === ReportStatuses.COMPLETED,
    (response) => response?.Status === ReportStatuses.FAILED
  );
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
  const [dotCounter, setDotCounter] = useState<number>(0);
  const [reportConfig, setReportConfig] =
    useState<ReportItem>(defaultReportItem);
  const [reportRequestParams, setReportRequestParams] = useState<
    Record<string, string | null>
  >({});
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const dropdownOptions: Option[][] = reportConfig?.allParamOptions || [];

  // Convert options to Ant Design Select format
  const selectOptionsGroups = useMemo(() => {
    return dropdownOptions.map((options) => ({
      selectorName: options[0]?.selectorName || "",
      options: options.map((opt) => ({
        key: opt.key,
        value: opt.key,
        label: opt.displayValue,
      })),
    }));
  }, [dropdownOptions]);

  const handleSelectChange = (value: string, selectorName: string): void => {
    setReportRequestParams((prevParams) => ({
      ...prevParams,
      [selectorName]: value,
    }));
  };

  /**
   * Sets up a dot counter for a loading indicator.
   * Increments the dot counter every 500ms, cycling through 0 to 3.
   */
  useEffect(() => {
    const intervalId = setInterval(() => {
      setDotCounter((prevCounter) => (prevCounter + 1) % 4);
    }, 500);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  /**
   * Fetches report parameters when the modal is shown.
   */
  useEffect(() => {
    if (show) {
      fetchReportParameters();
    }
  }, [show]);

  /**
   * Displays error toasts if there are errors in fetching report parameters, creating a report, or polling report status.
   */
  useEffect(() => {
    if (error) {
      addToast(MESSAGES.GET_FUND_NAMES_ERROR, Status.ERROR);
    }
    if (createReportError) {
      addToast(MESSAGES.DEFAULT_CREATE_REPORT_ERROR_MESSAGE, Status.ERROR);
    }
    if (reportJobStatusError) {
      addToast(MESSAGES.DEFAULT_CREATE_REPORT_ERROR_MESSAGE, Status.ERROR);
    }

    handleModalClose();
  }, [error, createReportError, reportJobStatusError]);

  /**
   * Sets report request parameters and configuration when report parameters change.
   */
  useEffect(() => {
    setReportRequestParams(reportParams?.requestParams || {});
    setReportConfig(reportParams || defaultReportItem);
  }, [reportParams]);

  /**
   * Starts polling for report status when report creation response is received.
   */
  useEffect(() => {
    if (triggerReportCreationResponse) {
      startPolling("CHECK_REPORT_STATUS", triggerReportCreationResponse);
    }
  }, [triggerReportCreationResponse]);

  /**
   * Sets the download URL and stops report generation when the report job status is received.
   * Closes the modal upon completion.
   */
  useEffect(() => {
    if (reportJobStatus) {
      setDownloadUrl(reportJobStatus.Url);
      setIsGeneratingReport(false);
      handleModalClose();
    }
  }, [reportJobStatus]);

  /**
   * Initiates file download when download URL is set.
   */
  useEffect(() => {
    if (downloadUrl) {
      downloadFile(downloadUrl);
    }
  }, [downloadUrl]);

  const handleModalClose = (): void => {
    setIsGeneratingReport(false);
    setReportConfig(defaultReportItem);
    handleClose();
  };

  const handleGenerateReport = async (): Promise<void> => {
    setIsGeneratingReport(true);
    await createReport();
  };

  const createReport = async (): Promise<void> => {
    triggerReportCreation("CREATE_REPORT", {
      Report: reportType,
      Parameters: { StringParameters: reportRequestParams },
    } as CreateReportParams);
  };

  // Derive modal state
  const modalState = isGeneratingReport
    ? ModalState.GENERATING
    : ModalState.IDLE;

  const { modalProps, isProcessing } = useCompassModal({
    config: CREATE_REPORT_MODAL_CONFIG,
    state: modalState,
    context: { reportType, dotCounter },
  });

  const downloadFile = (downloadUrl: string): void => {
    const link = document.createElement("a");

    link.href = downloadUrl;

    link.download = "filenameToSaveAs.ext";

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
  };

  const footerContent = (
    <ModalFooter
      onClose={handleModalClose}
      closeText="Cancel"
      submitText="Generate"
      onSubmit={(): void => form.submit()}
      isLoading={isProcessing}
      showDivider={false}
    />
  );

  return (
    <CompassModal
      show={show}
      onHide={handleClose}
      {...modalProps}
      footer={footerContent}
    >
      <ModalContent>
        <PreserveSpace
          hidden={isProcessing || selectOptionsGroups.length === 0}
        >
          <FormContainer>
            <StyledFormWrapper>
              <Form
                form={form}
                layout="vertical"
                size="large"
                requiredMark
                onFinish={handleGenerateReport}
              >
                {selectOptionsGroups.map((group, index) => (
                  <Form.Item
                    key={`${index}-${group.selectorName}-${isLoading}`}
                    name={group.selectorName}
                    label={
                      <span style={{ color: greyText }}>
                        {group.selectorName?.slice(1) || "Option"}
                      </span>
                    }
                    rules={[
                      {
                        required: true,
                        message: `Please select ${
                          group.selectorName?.slice(1) || "option"
                        }`,
                      },
                    ]}
                  >
                    <Select
                      placeholder={
                        isLoading
                          ? "Loading..."
                          : `Select ${group.selectorName?.slice(1) || "option"}`
                      }
                      options={group.options}
                      loading={isLoading}
                      disabled={isLoading}
                      onChange={(value): void =>
                        handleSelectChange(value as string, group.selectorName)
                      }
                    />
                  </Form.Item>
                ))}
              </Form>
            </StyledFormWrapper>
          </FormContainer>
        </PreserveSpace>
      </ModalContent>
    </CompassModal>
  );
};

interface ModalComponentProps {
  show: boolean;
  handleClose: () => void;
  reportType?: ReportCategory;
}
